import { supabase } from '../lib/supabase';

export const subscriptionService = {
  async getPlans() {
    try {
      const { data, error } = await supabase?.from('subscription_plans')?.select('*')?.order('price', { ascending: true })

      if (error) throw error
      
      // Deduplicate plans by tier using Map to ensure only one plan per tier
      const uniquePlansMap = new Map();
      data?.forEach(plan => {
        if (!uniquePlansMap?.has(plan?.tier)) {
          uniquePlansMap?.set(plan?.tier, plan);
        }
      });
      
      const uniquePlans = Array?.from(uniquePlansMap?.values()) || [];
      
      return { data: uniquePlans, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async getUserSubscription(userId) {
    try {
      const { data, error } = await supabase?.from('user_subscriptions')?.select(`
          *,
          subscription_plans (*)
        `)?.eq('user_id', userId)?.single()

      if (error && error?.code !== 'PGRST116') throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async getAgentLimitStatus(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_agent_limit_status', {
        p_user_id: userId
      })

      if (error) throw error
      return { data: data?.[0] || null, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async createCheckoutSession(priceId, userId, billingCycle = 'monthly', addons = []) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
        body: { priceId, userId, billingCycle, addons }
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async cancelSubscription(subscriptionId) {
    try {
      const { data, error } = await supabase?.from('user_subscriptions')?.update({ cancel_at_period_end: true })?.eq('id', subscriptionId)?.select()?.single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async submitCancellationSurvey(userId, subscriptionId, reason, feedback, retentionOfferAccepted = false) {
    try {
      const { data, error } = await supabase?.from('cancellation_surveys')?.insert({
        user_id: userId,
        subscription_id: subscriptionId,
        cancellation_reason: reason,
        feedback: feedback || null,
        retention_offer_shown: true,
        retention_offer_accepted: retentionOfferAccepted
      })?.select()?.single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async applyRetentionOffer(userId) {
    try {
      const { data, error } = await supabase?.rpc('apply_retention_offer', {
        p_user_id: userId,
        p_discount_percent: 20.00,
        p_duration_months: 3
      })

      if (error) throw error
      return { data: data?.[0] || null, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async pauseSubscription(userId, durationMonths) {
    try {
      const { data, error } = await supabase?.rpc('pause_subscription', {
        p_user_id: userId,
        p_duration_months: durationMonths
      })

      if (error) throw error
      return { data: data?.[0] || null, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async resumeSubscription(userId) {
    try {
      const { data, error } = await supabase?.rpc('resume_subscription', {
        p_user_id: userId
      })

      if (error) throw error
      return { data: data?.[0] || null, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async getPaymentFailures(userId) {
    try {
      const { data, error } = await supabase
        ?.from('payment_failures')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('resolved', false)
        ?.order('created_at', { ascending: false })

      if (error) throw error
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error }
    }
  },

  async calculateProration(userId, newPlanId, newBillingCycle = 'monthly') {
    try {
      const { data, error } = await supabase?.rpc('calculate_proration', {
        p_user_id: userId,
        p_new_plan_id: newPlanId,
        p_new_billing_cycle: newBillingCycle
      })

      if (error) throw error
      return { data: data?.[0] || null, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  async getUserAddons(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_addon_subscriptions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('status', 'active')

      if (error) throw error
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error }
    }
  },

  async getTotalAgentLimit(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_total_agent_limit', {
        p_user_id: userId
      })

      if (error) throw error
      return { data: data || 2, error: null }
    } catch (error) {
      return { data: 2, error }
    }
  },

  async getBillingSummary(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_billing_summary')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single()

      if (error && error?.code !== 'PGRST116') throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }
}

export default subscriptionService